/**
 * Reusable 1-5 score RadioGroup for session review.
 * Mobile: horizontal flex, min-h-11 (44px tap targets).
 * Color hint: 1-2 destructive, 3 muted, 4-5 success.
 */

const SCORE_OPTIONS = [1, 2, 3, 4, 5] as const;

const SCORE_LABELS: Record<number, string> = {
  1: 'Rất kém',
  2: 'Kém',
  3: 'Trung bình',
  4: 'Tốt',
  5: 'Xuất sắc',
};

function scoreButtonClass(n: number, selected: boolean): string {
  const base =
    'flex min-h-[44px] min-w-[44px] flex-col items-center justify-center rounded-md border text-xs font-semibold transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50';
  if (selected) {
    if (n <= 2) return `${base} border-destructive bg-destructive text-destructive-foreground`;
    if (n === 3) return `${base} border-border bg-muted text-foreground`;
    return `${base} border-brand-green-500 bg-brand-green-500 text-white`;
  }
  if (n <= 2) return `${base} border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/15`;
  if (n === 3) return `${base} border-border bg-background text-muted-foreground hover:bg-muted`;
  return `${base} border-brand-green-300 bg-brand-green-50 text-brand-green-700 hover:bg-brand-green-100 dark:border-brand-green-700 dark:bg-brand-green-950/30 dark:text-brand-green-300`;
}

interface ScoreInputProps {
  label: string;
  value: number | null | undefined;
  onChange: (v: number) => void;
  disabled?: boolean;
}

export function ScoreInput({ label, value, onChange, disabled }: ScoreInputProps) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex gap-1.5" role="radiogroup" aria-label={label}>
        {SCORE_OPTIONS.map((n) => {
          const selected = value === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`${n} — ${SCORE_LABELS[n]}`}
              disabled={disabled}
              onClick={() => onChange(n)}
              className={scoreButtonClass(n, selected)}
              title={SCORE_LABELS[n]}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}
